/**
 * Metric grouping + presentation helpers for the product card metrics section.
 *
 * Metrics are linked to a card by machine name, and A/B experiments emit one
 * series per arm: `<root>.treatment` and `<root>.control` (e.g.
 * `wfdt.success-rate.treatment` / `wfdt.success-rate.control`). When both arms
 * of the same root are linked we fold them into a single paired card so the
 * delta is legible; everything else (including `*.paired.*` rollups) renders
 * standalone.
 */
import type { ProductCardMetric } from '../hooks/useProductCardMetrics';

const ARMS = ['treatment', 'control'] as const;
type Arm = (typeof ARMS)[number];

export type MetricGroup =
  | { kind: 'single'; key: string; metric: ProductCardMetric }
  | {
      kind: 'pair';
      key: string;
      root: string;
      label: string;
      treatment: ProductCardMetric;
      control: ProductCardMetric;
    };

/** Split `<root>.<arm>` into its root + arm, or null when the name isn't an arm. */
function splitArm(name: string): { root: string; arm: Arm } | null {
  const idx = name.lastIndexOf('.');
  if (idx < 0) return null;
  const last = name.slice(idx + 1);
  const arm = ARMS.find((a) => a === last);
  if (!arm) return null;
  return { root: name.slice(0, idx), arm };
}

/** Display name for a metric: authored title, else a humanized machine name. */
export function metricTitle(metric: ProductCardMetric): string {
  const title = metric.title?.trim();
  if (title) return title;
  return humanizeSegment(metric.name.split('.').pop() ?? metric.name);
}

/** Humanize a dotted machine-name segment, e.g. "success-rate" → "Success Rate". */
function humanizeSegment(segment: string): string {
  return segment
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Fold A/B arm metrics into paired groups, preserving the order in which roots
 * first appear. A root only pairs when BOTH arms are present; a lone arm falls
 * through as a single so nothing silently disappears.
 */
export function groupMetrics(metrics: ProductCardMetric[]): MetricGroup[] {
  const byRoot = new Map<string, Partial<Record<Arm, ProductCardMetric>>>();
  for (const m of metrics) {
    const split = splitArm(m.name);
    if (!split) continue;
    const slot = byRoot.get(split.root) ?? {};
    slot[split.arm] = m;
    byRoot.set(split.root, slot);
  }

  const groups: MetricGroup[] = [];
  const consumed = new Set<string>();
  for (const m of metrics) {
    if (consumed.has(m.name)) continue;
    const split = splitArm(m.name);
    const arms = split ? byRoot.get(split.root) : undefined;
    if (split && arms?.treatment && arms.control) {
      consumed.add(arms.treatment.name);
      consumed.add(arms.control.name);
      // Last segment of the root is the metric being measured (e.g. "success-rate").
      const label = humanizeSegment(split.root.split('.').pop() ?? split.root);
      groups.push({
        kind: 'pair',
        key: `pair:${split.root}`,
        root: split.root,
        label,
        treatment: arms.treatment,
        control: arms.control,
      });
    } else {
      consumed.add(m.name);
      groups.push({ kind: 'single', key: `single:${m.name}`, metric: m });
    }
  }
  return groups;
}

type MetricTone = 'good' | 'bad' | 'neutral';

const TONE_CLASS: Record<MetricTone, string> = {
  good: 'text-[#15803d] dark:text-[#4ade80]',
  bad: 'text-[#dc2626] dark:text-[#f87171]',
  neutral: 'text-muted-foreground',
};

export function toneClass(tone: MetricTone): string {
  return TONE_CLASS[tone];
}

/**
 * Direction-aware tone for a numeric value. The semantics are inferred from the
 * metric name/unit since metrics are free-form: a points-delta is good when
 * positive, a duration ratio is good when below 1 (faster), and everything else
 * stays neutral so we never assert a direction we don't actually know.
 */
export function valueTone(metric: ProductCardMetric, value: number): MetricTone {
  const name = metric.name.toLowerCase();
  const unit = (metric.unit ?? '').toLowerCase();
  if (name.includes('delta') || unit === 'pp' || unit === 'points') {
    if (value > 0) return 'good';
    if (value < 0) return 'bad';
    return 'neutral';
  }
  if (name.includes('ratio')) {
    if (value < 1) return 'good';
    if (value > 1) return 'bad';
    return 'neutral';
  }
  return 'neutral';
}

/**
 * Tone for the treatment − control delta of a paired success-style metric.
 * Only rate/percentage metrics carry a meaningful direction (higher = better);
 * volume metrics like run-count return neutral.
 */
export function pairDeltaTone(root: string, unit: string | null, delta: number): MetricTone {
  const r = root.toLowerCase();
  const isRate =
    (unit ?? '').toLowerCase() === 'percent' ||
    r.includes('success') ||
    r.includes('rate') ||
    r.includes('pass');
  if (!isRate) return 'neutral';
  if (delta > 0) return 'good';
  if (delta < 0) return 'bad';
  return 'neutral';
}

/** Trim a raw scalar to ~4 significant figures (e.g. 1.029309… → "1.029"). */
export function formatScalar(v: number): string {
  return Number.isInteger(v) ? String(v) : Number(v.toPrecision(4)).toString();
}

/** Latest value + unit for display, or a sentinel for empty/error states. */
export function formatLatest(metric: ProductCardMetric): { value: string; unit: string | null } {
  if (metric.lastError) return { value: 'error', unit: null };
  if (metric.lastValue === null || metric.lastValue === undefined) {
    return { value: '—', unit: null };
  }
  return { value: formatScalar(metric.lastValue), unit: metric.unit };
}
